import { getOpenPublicationLabel } from '@/components/publication/publicationView';
import type { ExtractedStatement } from '@/features/reference-assistant/model';
import type { MessagePublication } from '@/services/getMessagePublicationsV2';
import type { PublicationShareEntry } from '@/types/messages/base';
import { formatReferenceUrlForDisplay } from '@/util/chat/referenceUtils';

/**
 * One numbered reference on a message, as the footer lists it and the
 * reference sheet pages through it.
 *
 * `number` is the server's, from the message's share entries. `url` is what
 * opens: the catalog link for the share. `statementIndices` are the
 * statements the work is attached to, and `statementIndex` and
 * `statementType` describe the first of them for analytics.
 */
export interface MessageReference {
  number: number;
  url: string;
  publication?: MessagePublication;
  statementIndices?: readonly number[];
  statementIndex?: number;
  statementType?: string;
}

/**
 * The message's references in the server's order, each entry joined to its
 * publication's details by share id. An entry whose publication has not
 * loaded is left out until it does, since there is nothing yet to show or
 * open; its chips in the text already carry the right number.
 */
export function buildMessageReferences(
  entries: readonly PublicationShareEntry[],
  publications: readonly MessagePublication[],
  extractedStatements: readonly ExtractedStatement[] = []
): MessageReference[] {
  const publicationByShare = new Map(
    publications.map((publication) => [publication.shareId, publication])
  );

  return entries.flatMap((entry) => {
    const publication = publicationByShare.get(entry.shareId);

    if (!publication) {
      return [];
    }

    const statementIndex = entry.statementIndices[0];

    return [
      {
        number: entry.number,
        url: publication.url,
        publication,
        statementIndices: entry.statementIndices,
        statementIndex,
        statementType:
          statementIndex === undefined
            ? undefined
            : extractedStatements[statementIndex]?.statementType,
      },
    ];
  });
}

/** The article's name, or the link when the catalog has no title for it. */
export function getReferenceTitle(reference: MessageReference): string {
  const title = reference.publication?.title?.trim().replace(/\.$/, '');

  return title || formatReferenceUrlForDisplay(reference.url);
}

/**
 * The footnote row's label. A study cited only by name leads with the name the
 * author wrote, because the registry title it was matched to usually omits it
 * — the row should read as the "CARTITUDE-4" the reader saw in the message.
 */
export function getReferenceRowLabel(reference: MessageReference): string {
  const mentionText = reference.publication?.mentionText?.trim();

  const title = getReferenceTitle(reference);

  return mentionText ? `${mentionText} · ${title}` : title;
}

const MENTION_NOUNS: Record<MessagePublication['kind'], string> = {
  article: 'study',
  trial: 'trial',
  nccn_guideline: 'guideline',
  web: 'source',
};

/**
 * Where a reference came from, when the reader could otherwise mistake it for
 * a citation the author chose. A study only named in prose is matched to a
 * record by Primum, and the registry title rarely contains the name the author
 * wrote — "CARTITUDE-4" appears nowhere in its official title — so without
 * this line an author reads it as a citation they never made. It is kept even
 * when the author ruled the study out, by product decision, which is exactly
 * when the explanation matters most.
 *
 * Other readers see "The message", not a role: shares ride on any message, so
 * the author can be the expert or the submitter, and naming the wrong one
 * credits a person with words they never wrote.
 */
export function getReferenceProvenance(
  reference: MessageReference,
  viewerIsAuthor: boolean
): string | undefined {
  if (reference.publication?.origin !== 'named_mention') {
    return undefined;
  }

  const noun = MENTION_NOUNS[reference.publication.kind];

  const who = viewerIsAuthor ? 'You' : 'The message';

  const mentionText = reference.publication.mentionText?.trim();

  const named = mentionText ? `“${mentionText}”` : `this ${noun}`;

  return `${who} mentioned ${named} by name. Primum found the matching record.`;
}

export function getOpenReferenceLabel(reference: MessageReference): string {
  return getOpenPublicationLabel(reference.publication?.kind);
}
